import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  XAxis,
  Legend,
  Tooltip,
  Customized,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  ReferenceLine,
  YAxis,
} from "recharts";

import { IPatientBaseline } from "../../@types/Baselines";
import { IDateRangeFilter } from "../../@types/Preferences";
import { css } from "../../styles";
import { calculateLinearRegression } from "../../utils/chartUtils";
import {
  getBloodPressurePairedValues,
  getDataUnformattedtimeAndValues,
} from "../../utils/measurementsUtils";
import {
  BaselineTooltipOverlay,
  IBaselineSegment,
} from "../BaselineTooltip/BaselineTooltipOverlay";
import { CustomChartLegend } from "../CustomChartLegend/CustomChartLegend";
import { CustomRechartsDot } from "../CustomRechartsDot/CustomRechartsDot";
import { CustomRechartsTooltip } from "../CustomRechartsTooltip/CustomRechartsTooltip";
import {
  ChartTitle,
  StyledTile,
  StyledSubtitle,
  EmptyStateOverlay,
} from "./ChartCard.styles";
import { ChartCardValue, ChartData, ChartConfig } from "./ChartCard.types";
import { ChartCardLoading } from "./ChartCardLoading";
import { ChartSpecificValues } from "./ChartSpecificValues/ChartSpecificValues";

export type ChartCardProps = {
  title: string;
  data: Map<string, ChartData[]> | Map<string, any[]>;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  isLoading?: boolean;
  showTrendLine?: boolean;
  chartConfig: ChartConfig[];
  specificValues: ChartCardValue[];
  patientBaselines: IPatientBaseline[];
  minValue?: number;
  maxValue?: number;
  dateRange?: IDateRangeFilter;
};

export function ChartCard({
  data,
  title,
  onClick,
  isLoading,
  chartConfig,
  specificValues,
  showTrendLine,
  patientBaselines,
  minValue,
  maxValue,
  dateRange,
}: ChartCardProps) {
  const { t } = useTranslation();
  const isEmptyState = (!data || !data.size) && patientBaselines.length === 0;

  const { yKey } = chartConfig[0] ?? {};

  const calculateMeasurementLinearRegression = () => {
    const { xValues, yValues } = getDataUnformattedtimeAndValues(data, yKey);

    return calculateLinearRegression(xValues, yValues);
  };

  const calculateBloodPressureLinearRegression = () => {
    const { xValues, yValues } = getBloodPressurePairedValues(data);

    const filteredSystolic = yValues.systolic.filter(
      (v): v is number => v !== undefined
    );
    const filteredDiastolic = yValues.diastolic.filter(
      (v): v is number => v !== undefined
    );

    return {
      diastolic: calculateLinearRegression(xValues, filteredDiastolic),
      systolic: calculateLinearRegression(xValues, filteredSystolic),
    };
  };

  const chartCss = css({
    ".recharts-surface": {
      marginTop: "12px",
    },
  });

  const mergedDataSources = !isEmptyState
    ? Array.from(data.values()).flat()
    : [];

  // X-axis domain must follow the selected date range so points span the full
  // width — same as ModalChart.handleXDomain. Without it the card fell back to a
  // fixed ~2-month window, squashing short ranges against the right edge.
  const getXDomain = (): [number, number] => {
    if (!dateRange) {
      return [
        dayjs().add(-2, "month").valueOf(),
        dayjs().add(7, "day").valueOf(),
      ];
    }

    const { startDate } = dateRange;
    let { endDate } = dateRange;

    // Very long ranges (e.g. "ALL") fit to the actual data extent.
    if (endDate.diff(startDate, "year") > 5) {
      let min: number | undefined;
      let max: number | undefined;
      mergedDataSources.forEach((point) => {
        const time = Number(point.unformattedTime);
        if (!Number.isFinite(time)) return;
        if (min === undefined || time < min) min = time;
        if (max === undefined || time > max) max = time;
      });

      if (min !== undefined && max !== undefined) {
        return [min, max];
      }
    }

    // Long ranges get a little right padding, matching the modal.
    if (endDate.diff(startDate, "month") > 11) {
      endDate = endDate.add(30, "day");
    }

    return [startDate.valueOf(), endDate.valueOf()];
  };

  const baselineSegments: IBaselineSegment[] = useMemo(() => {
    if (!patientBaselines) return [];
    return patientBaselines.map((patientBaseline) => {
      const rangeStart = dateRange?.startDate ?? dayjs().add(-2, "month");
      const rangeEnd = dateRange?.endDate ?? dayjs();

      const handleEndSegmentXAxis = () => {
        if (patientBaseline.archivedDate !== undefined) {
          return dayjs(patientBaseline.archivedDate).valueOf();
        }

        const archiveDateDayjs = dayjs(patientBaseline.archivedDate);

        if (dayjs().diff(archiveDateDayjs, "day") < 30) {
          return rangeEnd.add(7, "day").valueOf();
        }

        return rangeEnd.valueOf();
      };

      const handleStartSegmentXAxis = () => {
        if (dayjs(patientBaseline.created).diff(rangeStart) < 0) {
          return rangeStart.valueOf();
        }

        return dayjs(patientBaseline.created).valueOf();
      };

      const segXStart = handleStartSegmentXAxis();
      const segXEnd = handleEndSegmentXAxis();

      return {
        baseline: patientBaseline,
        xStart: Math.min(segXStart, segXEnd),
        xEnd: Math.max(segXStart, segXEnd),
        yValue: patientBaseline.baseline,
      };
    });
  }, [patientBaselines, dateRange]);

  return (
    <StyledTile onClick={onClick}>
      {isLoading ? (
        <ChartCardLoading />
      ) : (
        <>
          <ChartTitle>{title}</ChartTitle>

          {isEmptyState && (
            <StyledSubtitle>
              {t(
                title === "-"
                  ? "common_noDataToShow"
                  : "patientPage_card_noData"
              )}
            </StyledSubtitle>
          )}
          {isEmptyState && <EmptyStateOverlay />}

          <ResponsiveContainer
            width="100%"
            height={isEmptyState ? "57%" : "67%"}
          >
            <ScatterChart
              data={mergedDataSources}
              style={{ cursor: "pointer" }}
              className={chartCss()}
            >
              {!isEmptyState && (
                <Legend
                  height={80}
                  verticalAlign="top"
                  content={
                    <CustomChartLegend
                      baseline={
                        patientBaselines.filter(
                          (pb) => pb.isISHLTBaseline === false
                        ).length > 0
                      }
                      ishltBaseline={
                        patientBaselines.filter(
                          (pb) => pb.isISHLTBaseline === true
                        ).length > 0
                      }
                    />
                  }
                />
              )}
              <Tooltip
                cursor={false}
                content={<CustomRechartsTooltip data={mergedDataSources} />}
                wrapperStyle={{ outline: "none" }}
                labelFormatter={() => undefined}
              />
              <YAxis
                hide
                domain={
                  minValue != null &&
                  !Number.isNaN(minValue) &&
                  maxValue != null &&
                  !Number.isNaN(maxValue)
                    ? [minValue, maxValue]
                    : ["auto", "auto"]
                }
              />
              <XAxis
                hide
                dataKey="unformattedTime"
                domain={getXDomain()}
                type="number"
                allowDuplicatedCategory={false}
              />
              {showTrendLine && (
                <>
                  <ReferenceLine
                    stroke="#285FCA"
                    strokeDasharray="15 15"
                    strokeWidth={2}
                    segment={
                      yKey !== "bp_systolic"
                        ? calculateMeasurementLinearRegression()
                        : calculateBloodPressureLinearRegression().systolic
                    }
                    ifOverflow="hidden"
                  />
                  <ReferenceLine
                    stroke="#285FCA"
                    strokeDasharray="15 15"
                    strokeDashoffset={15}
                    strokeOpacity={0.57}
                    strokeWidth={2}
                    segment={
                      yKey !== "bp_systolic"
                        ? calculateMeasurementLinearRegression()
                        : calculateBloodPressureLinearRegression().systolic
                    }
                    ifOverflow="hidden"
                  />
                  {yKey === "bp_systolic" && (
                    <>
                      <ReferenceLine
                        stroke="#285FCA"
                        strokeDasharray="15 15"
                        strokeOpacity={0.4}
                        strokeWidth={2}
                        segment={
                          yKey !== "bp_systolic"
                            ? calculateMeasurementLinearRegression()
                            : calculateBloodPressureLinearRegression().diastolic
                        }
                        ifOverflow="hidden"
                      />
                      <ReferenceLine
                        stroke="#285FCA"
                        strokeDasharray="15 15"
                        strokeDashoffset={15}
                        strokeOpacity={0.57}
                        strokeWidth={2}
                        segment={
                          yKey !== "bp_systolic"
                            ? calculateMeasurementLinearRegression()
                            : calculateBloodPressureLinearRegression().diastolic
                        }
                        ifOverflow="hidden"
                      />
                    </>
                  )}
                </>
              )}

              {baselineSegments.length > 0 &&
                baselineSegments.map((seg) => (
                  <ReferenceLine
                    key={`baseline-ref-${seg.baseline.id}`}
                    stroke={
                      seg.baseline.isISHLTBaseline ? "#754164" : "#58C488"
                    }
                    strokeDasharray="10 8"
                    strokeWidth={2}
                    segment={[
                      { x: seg.xStart, y: seg.yValue },
                      { x: seg.xEnd, y: seg.yValue },
                    ]}
                    ifOverflow="extendDomain"
                  />
                ))}

              {!isEmptyState &&
                chartConfig.map(
                  (currentChartConfig: ChartConfig, index: number) => {
                    const {
                      yKey,
                      stroke,
                      dotShape,
                      color,
                      lineOpacity,
                      source,
                    } = currentChartConfig;

                    const dataSource = source
                      ? data.get(`${yKey}|${source}`)
                      : [];

                    return (
                      <Scatter
                        key={`${yKey}_${source}_${index}`}
                        isAnimationActive={false}
                        fill={color}
                        line={
                          !showTrendLine
                            ? {
                                stroke: color,
                                strokeWidth: stroke || 1.4,
                                opacity: lineOpacity,
                              }
                            : undefined
                        }
                        lineJointType="monotoneX"
                        shape={
                          <CustomRechartsDot fill={color} shape={dotShape} />
                        }
                        dataKey={yKey}
                        name={source}
                        data={dataSource}
                      />
                    );
                  }
                )}

              {baselineSegments.length > 0 && (
                <Customized
                  component={
                    <BaselineTooltipOverlay
                      baselineSegments={baselineSegments}
                    />
                  }
                />
              )}
            </ScatterChart>
          </ResponsiveContainer>
          <ChartSpecificValues values={specificValues} />
        </>
      )}
    </StyledTile>
  );
}
